/**
 * QUICK FIX MODE
 *
 * Streamlined interface for small fixes and quick chat launches.
 * Quick Fix creates a task (and optionally a GitHub issue);
 * Plan/Agent modes open an empty chat run.
 */

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Zap,
  CheckCircle,
  Loader2,
  ExternalLink,
  AlertCircle,
  Bot,
  FileText,
} from "lucide-react";
import {
  WorkflowSelector,
  COLONY_AGENT_SENTINEL,
  isColonyAgent,
  getWorkflowDisplayName,
  workflowIssueTypeToTaskIssueType,
  filterSavedWorkflows,
} from "@/components/WorkflowSelector";
import { useWorldStore, type Task } from "@/stores/worldStore";
import { useToast } from "@/hooks/use-toast";
import { useWorkflowStore } from "@/stores/workflowStore";
import {
  getRequiredStartFields,
  getStartFieldLabel,
} from "@/lib/workflow/startFields";

type QuickFixStatus = "idle" | "creating" | "done" | "failed";
type QuickFixMode = "quick_fix" | "plan" | "agent";
type ChatLaunchMode = "plan" | "agent";

interface QuickFixProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: number;
  projectName?: string;
  projects?: { id: number; name: string }[];
  enableChatModes?: boolean;
  onStartChat?: (mode: ChatLaunchMode, context: { projectId?: number; projectName?: string }) => void;
}

function generateTitle(prompt: string): string {
  const firstLine = prompt.split("\n")[0].trim();
  return firstLine.length > 80 ? firstLine.slice(0, 77) + "..." : firstLine;
}

export function QuickFix({
  isOpen,
  onClose,
  projectId,
  projectName,
  projects = [],
  enableChatModes = false,
  onStartChat,
}: QuickFixProps) {
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState<QuickFixMode>("quick_fix");
  const [status, setStatus] = useState<QuickFixStatus>("idle");
  const [createdTask, setCreatedTask] = useState<Task | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string>(projectId ? projectId.toString() : "");
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>(COLONY_AGENT_SENTINEL);

  const addTask = useWorldStore((s) => s.addTask);
  const workflows = useWorkflowStore((s) => s.workflows);
  const fetchWorkflows = useWorkflowStore((s) => s.fetchWorkflows);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      void fetchWorkflows();
    }
  }, [isOpen, fetchWorkflows]);

  const savedWorkflows = useMemo(() => filterSavedWorkflows(workflows), [workflows]);
  const selectedWorkflow = useMemo(
    () => savedWorkflows.find((workflow) => workflow.id === selectedWorkflowId),
    [savedWorkflows, selectedWorkflowId],
  );
  const selectedWorkflowDisplayName = useMemo(
    () => {
      if (isColonyAgent(selectedWorkflowId)) return "Colony Agent (Automatic)";
      return selectedWorkflow ? getWorkflowDisplayName(selectedWorkflow) : "";
    },
    [selectedWorkflowId, selectedWorkflow],
  );
  const selectedStartNode = useMemo(
    () => selectedWorkflow?.nodes.find((node) => node.type === "start"),
    [selectedWorkflow],
  );
  const requiredStartFields = useMemo(
    () => {
      const explicitConfig = selectedStartNode?.config?.startRequiredFields;
      const quickFixDefaults = ["title", "description"];
      const source = Array.isArray(explicitConfig) ? explicitConfig : quickFixDefaults;
      return getRequiredStartFields(source, selectedWorkflow?.issueType);
    },
    [selectedStartNode, selectedWorkflow],
  );
  const unsupportedRequiredFields = useMemo(
    () => requiredStartFields.filter((field) => field !== "title" && field !== "description"),
    [requiredStartFields],
  );
  const hasUnsupportedRequiredFields = unsupportedRequiredFields.length > 0;

  useEffect(() => {
    if (!isOpen) return;
    setSelectedWorkflowId((current) => {
      if (isColonyAgent(current)) return current;
      if (current && savedWorkflows.some((workflow) => workflow.id === current)) return current;
      return COLONY_AGENT_SENTINEL;
    });
  }, [isOpen, savedWorkflows]);

  const hasProjectSelected = projectId || selectedProject;
  const resolvedProjectId = projectId || (selectedProject ? parseInt(selectedProject, 10) : null);
  const currentProjectName = projectId ? projectName : projects.find(p => p.id.toString() === selectedProject)?.name;
  const isQuickFixMode = mode === "quick_fix";
  const hasWorkflowSelected = isColonyAgent(selectedWorkflowId) || !!selectedWorkflow;
  const submitDisabled = isQuickFixMode
    ? !prompt.trim() || !hasProjectSelected || !hasWorkflowSelected || hasUnsupportedRequiredFields
    : false;
  const submitLabel = isQuickFixMode ? "Start Quick Fix" : mode === "plan" ? "Open Plan Chat" : "Open Agent Chat";

  const handleReset = () => {
    setPrompt("");
    setMode("quick_fix");
    setStatus("idle");
    setCreatedTask(null);
    setError(null);
    if (!projectId) {
      setSelectedProject("");
    }
  };

  const handleSubmit = async () => {
    if (mode !== "quick_fix") {
      onStartChat?.(mode, {
        projectId: resolvedProjectId ?? undefined,
        projectName: currentProjectName,
      });
      toast({
        title: mode === "plan" ? "Plan chat opened" : "Agent chat opened",
        description: "Started an empty chat without creating a GitHub issue.",
      });
      handleReset();
      onClose();
      return;
    }

    if (!prompt.trim() || !hasProjectSelected || !resolvedProjectId || !hasWorkflowSelected) return;

    setError(null);
    setStatus("creating");

    try {
      const isColony = isColonyAgent(selectedWorkflowId);
      // QuickFix is bug-centric, so Colony Agent defaults to "bug" here.
      // NewTaskModal defaults Colony Agent to "feature" (general-purpose).
      const issueType = isColony
        ? ("bug" as Task["issueType"])
        : workflowIssueTypeToTaskIssueType(selectedWorkflow!.issueType);
      const title = generateTitle(prompt);

      const result = await addTask(
        {
          projectId: resolvedProjectId,
          title,
          description: prompt,
          metadata: {
            startInput: {
              title,
              description: prompt,
            },
            ...(isColony && { workflowType: "COLONY_AGENT" }),
          },
          pipelineStage: "backlog",
          status: "pending",
          issueType,
          ...(selectedWorkflow && { workflowId: selectedWorkflow.id }),
        },
        resolvedProjectId,
      );

      if (result) {
        setCreatedTask(result);
        setStatus("done");
        toast({
          title: "Task created",
          description: result.githubIssueUrl
            ? "GitHub issue created and added to backlog"
            : "Task added to backlog",
        });
      } else {
        throw new Error("Task creation returned null");
      }
    } catch (err) {
      setStatus("failed");
      const message = err instanceof Error ? err.message : "Failed to create task";
      setError(message);
      toast({
        title: "Quick Fix failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Zap className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle>Quick Fix</DialogTitle>
              <DialogDescription>
                Fast path for quick fixes, plan chats, and agent chats
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {status === "idle" && (
            <>
              {!projectId && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">{isQuickFixMode ? "Project *" : "Project (optional)"}</label>
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id.toString()}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {enableChatModes && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Mode</label>
                  <Select value={mode} onValueChange={(v) => setMode(v as QuickFixMode)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quick_fix">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-primary" />
                          Quick Fix
                        </div>
                      </SelectItem>
                      <SelectItem value="plan">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-500" />
                          Plan
                        </div>
                      </SelectItem>
                      <SelectItem value="agent">
                        <div className="flex items-center gap-2">
                          <Bot className="w-4 h-4 text-green-500" />
                          Agent
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {!isQuickFixMode && (
                    <p className="text-xs text-muted-foreground">
                      Opens an empty chat without creating a task or GitHub ticket.
                    </p>
                  )}
                </div>
              )}

              {isQuickFixMode && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Workflow *</label>
                    <WorkflowSelector
                      value={selectedWorkflowId}
                      onChange={setSelectedWorkflowId}
                      workflows={savedWorkflows}
                    />
                    {!isColonyAgent(selectedWorkflowId) && !selectedWorkflow && savedWorkflows.length === 0 && (
                      <p className="text-xs text-amber-500">
                        No saved workflows found. Create and save one first in Agent Builder.
                      </p>
                    )}
                    {selectedWorkflow && hasUnsupportedRequiredFields && (
                      <p className="text-xs text-amber-500">
                        This workflow requires extra intake fields ({unsupportedRequiredFields.map(getStartFieldLabel).join(", ")}).
                        Use New Agent instead of Quick Fix.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Describe the fix</label>
                    <Textarea
                      placeholder="e.g., Fix the null pointer exception in getUserProfile when user.email is undefined"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      rows={4}
                      className="resize-none"
                    />
                    <p className="text-xs text-muted-foreground">
                      Be specific about the problem and expected behavior
                    </p>
                  </div>
                </>
              )}

              {currentProjectName && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Project:</span>
                  <Badge variant="secondary">{currentProjectName}</Badge>
                </div>
              )}

              <Button
                className="w-full gap-2"
                onClick={handleSubmit}
                disabled={submitDisabled}
              >
                {isQuickFixMode ? <Zap className="w-4 h-4" /> : mode === "plan" ? <FileText className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                {submitLabel}
              </Button>
            </>
          )}

          {status === "creating" && (
            <div className="py-8">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="w-12 h-12 animate-spin text-primary" />
                <div className="text-center">
                  <p className="font-medium">Creating task...</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Setting up GitHub issue and backlog entry
                  </p>
                </div>
              </div>
            </div>
          )}

          {status === "done" && (
            <div className="py-8">
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 rounded-full bg-green-500/10">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-lg">Task Created</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Added to backlog with {selectedWorkflowDisplayName || "selected workflow"}
                  </p>
                </div>
                {createdTask?.githubIssueUrl && (
                  <a
                    href={createdTask.githubIssueUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View GitHub Issue
                  </a>
                )}
                <Button onClick={handleReset} className="gap-2">
                  <Zap className="w-4 h-4" />
                  Another Quick Fix
                </Button>
              </div>
            </div>
          )}

          {status === "failed" && (
            <div className="py-8">
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 rounded-full bg-red-500/10">
                  <AlertCircle className="w-12 h-12 text-red-500" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-lg">Something went wrong</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {error || "The task couldn't be created. Please try again."}
                  </p>
                </div>
                <Button onClick={handleReset} variant="outline">
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
